import { createJSONStorage, devtools, persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

import { create } from "@/lib/store";
import { BusinessUnitToggle, GeoViewToggle, MetricType } from "@/shared/types";
import { hashStorage } from "@/shared/utils/hashStorage";

import { DEFAULT_COLUMNS } from "../constants/defaults";

const STORE_NAME = "CFO";

export enum ROIViewToggle {
  SUMMARY = "summary",
  BREAKDOWN = "breakdown",
}

interface FinancialState {
  targetNodeUUID: string | null;
  filters: {
    geographies: string[];
  };
  grid: {
    geoView: GeoViewToggle;
    businessUnitToggle: BusinessUnitToggle;
    activeColumns: string[];
    metricType: MetricType;
  };
  roiViewToggle: ROIViewToggle;
}

interface FinancialActions {
  setTargetNodeUUID: (nodeUUID: string | null) => void;
  setGeographies: (geographies: string[]) => void;
  setBusinessUnitToggle: (toggle: BusinessUnitToggle) => void;
  setGeoView: (geoView: GeoViewToggle) => void;
  setActiveColumns: (columns: string[]) => void;
  setMetricType: (metricType: MetricType) => void;
  setROIViewToggle: (toggle: ROIViewToggle) => void;
}

type FinancialStore = FinancialState & FinancialActions;

const defaultState: FinancialState = {
  targetNodeUUID: null,
  filters: {
    geographies: [],
  },
  grid: {
    geoView: GeoViewToggle.AGGREGATE,
    businessUnitToggle: BusinessUnitToggle.AGGREGATE,
    activeColumns: DEFAULT_COLUMNS,
    metricType: MetricType.SALES_REVENUE,
  },
  roiViewToggle: ROIViewToggle.SUMMARY,
};

export const useFinancialStore = create<FinancialStore>()(
  devtools(
    persist(
      immer((set) => ({
        ...defaultState,
        setTargetNodeUUID: (nodeUUID) =>
          set((state) => {
            state.targetNodeUUID = nodeUUID;
          }),
        setGeographies: (geographies) =>
          set((state) => {
            state.filters.geographies = geographies;
          }),
        setBusinessUnitToggle: (businessUnitToggle) =>
          set((state) => {
            state.grid.businessUnitToggle = businessUnitToggle;
          }),
        setGeoView: (geoView) =>
          set((state) => {
            state.grid.geoView = geoView;
          }),
        setActiveColumns: (columns) =>
          set((state) => {
            state.grid.activeColumns = columns;
          }),
        setMetricType: (metricType) =>
          set((state) => {
            state.grid.metricType = metricType;
          }),
        setROIViewToggle: (toggle) =>
          set((state) => {
            state.roiViewToggle = toggle;
          }),
      })),
      {
        name: STORE_NAME,
        storage: createJSONStorage(() => hashStorage),
      }
    ),
    { name: STORE_NAME }
  )
);
